import { FormGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { LanguageOptions } from '@common/enums';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { MedicalSpecialistSpecialismCode } from '@common/enums/specialismCode';
import { computeSelectProps } from '@frontend/components/fields/SelectField';
import { getSpecialisms } from '@frontend/services/api/specialismsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import { getSpecialismSelectOptions } from '@frontend/utils/getSpecialismSelectOptions';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IOption } from '../interfaces/option';
import type { ISpecialism } from '../interfaces/specialism';

type SpecialismOption = IOption<string>;

interface SpecialismSelectorProps {
    className?: string;
    errors: ValidationErrors;
    onChange: (value: string | null) => void;
    value: string | null;
}

export function SpecialismSelector(props: Readonly<SpecialismSelectorProps>) {
    const abortSignal = useAbortSignal();
    const [selectedSpecialism, setSelectedSpecialism] = useState<string | null>(null);
    const [specialismOptions, setSpecialismOptions] = useState<SpecialismOption[]>([]);
    const language = useSelector((state: IReduxState) => state.language.code) as LanguageOptions;
    const translate = getSelectedLanguage();

    useEffect(() => {
        loadSpecialisms();
    }, []);

    return (
        <FormGroup
            hasErrors={props.errors.has('employment.specialismCode')}
            isRequired={true}
            label={translate.common.specialism}
        >
            <Select
                {...computeSelectProps({
                    className: props.className,
                    error: getValidationErrorTranslation(props.errors.get('employment.specialismCode')),
                    onChange: handleChange,
                    options: specialismOptions,
                    placeholder: '',
                    value: selectedSpecialism,
                })}
            />
        </FormGroup>
    );

    function handleChange(value: string | null) {
        setSelectedSpecialism(value);
        props.onChange(value);
    }

    async function loadSpecialisms() {
        const result = await getSpecialisms(language, abortSignal);

        const specialisms = result.filter(
            (item: ISpecialism) =>
                item.isActive &&
                Object.values(MedicalSpecialistSpecialismCode).includes(item.code as MedicalSpecialistSpecialismCode)
        );

        const specialismSelectOptions = getSpecialismSelectOptions(specialisms, false);

        setSpecialismOptions(
            specialismSelectOptions.map((specialism) => ({ label: specialism.text, value: specialism.value }))
        );
    }
}
